import { Button, Snackbar, Typography } from 'components';
import { useTranslations } from 'next-intl';
import { usePaymentMethodAttach } from 'providers/BillingInfoProviders/usePaymentMethodAttach';
import { useSetupIntentCreate } from 'providers/BillingInfoProviders/useSetupIntentCreate';
import { ComponentType, FC, FunctionComponent, useEffect, useState } from 'react';
import { CardElement, injectStripe } from 'react-stripe-elements';

import 'components/Inputs/input.scss';
import { onErrorHelper } from 'handlers/handlerMutations';
import { toaster } from 'helpers/toaster';
import { useCreditCard } from 'providers/BillingInfoProviders/useCreditCard';
import { inputPaymentStyle } from 'styles/inputPaymentStyle';
import { TGenericError } from 'types/TGenericError';
import { useDebouncedCallback } from 'use-debounce';

interface ICreditCardForm {
  cancel: () => void;
  stripe?: any;
  updateCreditCard: (creditCard: any) => void;
}

const CreditCardForm: FC<ICreditCardForm> = ({ cancel, stripe, updateCreditCard }) => {
  const t = useTranslations();
  const [state, setState] = useState({
    complete: false,
    error: null as { message: string } | null,
    savingInfo: false,
    clientSecret: null as null | string
  });

  const { refetch } = useCreditCard();

  const { mutateAsync: setupIntentCreate } = useSetupIntentCreate({
    onSuccess: (response) => {
      if (response.success)
        setState((oldState) => ({ ...oldState, clientSecret: response?.clientSecret }));
    },
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const { mutateAsync: paymentMethodAttach } = usePaymentMethodAttach({
    onSuccess: () => toaster.success(t('messages.success.payment_attached')),
    onError: onErrorHelper as (data: TGenericError) => void
  });

  const handlerOnCancel = () => cancel();

  const handleOnChange = ({
    complete,
    error
  }: {
    complete: boolean;
    error: { message: string } | null;
  }) => setState((oldState) => ({ ...oldState, complete, error }));

  const saveCreditCardDetails = async () => {
    setState((oldState) => ({ ...oldState, savingInfo: true }));

    const { error, setupIntent } = await stripe?.handleCardSetup(state?.clientSecret);

    if (error) return setState((oldState) => ({ ...oldState, savingInfo: false, error }));

    try {
      setState((oldState) => ({ ...oldState, savingInfo: true }));
      await paymentMethodAttach({ setupIntent: setupIntent?.id });
      const creditCard = await paymentMethodAttach({ setupIntent: setupIntent?.id });
      await refetch();
      updateCreditCard(creditCard);
      setState((oldState) => ({ ...oldState, savingInfo: false }));
    } catch (e) {
      setState((oldState) => ({ ...oldState, savingInfo: false }));
    }
  };

  const debouncedMutation = useDebouncedCallback(setupIntentCreate, 1000);

  useEffect(() => {
    debouncedMutation({});
  }, [debouncedMutation]);

  return (
    <>
      <label className="mb-2 ">
        <Typography variant="regular4" className="text-tertiary-100 ">
          {t('labels.card_number')}
        </Typography>
      </label>
      <div className="input-primary pt-[0.60rem]">
        <CardElement style={inputPaymentStyle} onChange={handleOnChange} />
      </div>

      {state.error && (
        <Snackbar description={state?.error?.message} variant="danger" className="mt-4" />
      )}

      <div className=" flex justify-between">
        <Button
          label={t('credit_card.cancel')}
          onClick={handlerOnCancel}
          size="medium"
          className="!mt-3 !flex !items-center !px-0"
          variant="text"
        />
        <Button
          label={t('credit_card.save')}
          onClick={saveCreditCardDetails}
          size="medium"
          disabled={!state.complete || state.savingInfo}
          className="!mt-3 !flex !items-center"
          variant="contained"
        />
      </div>
    </>
  );
};

export default injectStripe(
  CreditCardForm as unknown as FunctionComponent
) as ComponentType<ICreditCardForm>;
